// Deterministic primitives for the DAEMON simulation.
//
// Everything in here exists so that a run can be described by
// `{ seed, input stream }` and replayed to the same frame on a
// different machine, in a different browser, next year. Two things
// break that if you let them:
//
//   1. `Math.random()` — unseedable by spec.
//   2. `Math.sin` / `Math.cos` — ECMAScript deliberately does NOT
//      specify exact results, so engines legitimately disagree in the
//      low bits. Over a few thousand ticks that compounds into a
//      visibly different run, which means a replay recorded in Firefox
//      desyncs in Chrome.
//
// So the sim never calls either. It calls `Rng` and the trig tables
// below, both of which are pure integer/float arithmetic with
// identical results everywhere.

/**
 * Hashes a human-readable seed string into four well-distributed 32-bit
 * values for `sfc32`. Seeding a PRNG directly from a string's char
 * codes gives correlated streams for similar strings ("run-1" and
 * "run-2" would start near each other); xmur3 avalanches them apart.
 */
export function xmur3(str: string): () => number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return (): number => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

/** A seeded random source. Call it for a float in [0, 1). */
export type Rng = {
  /** Next float in [0, 1). */
  (): number;
  /** Integer in [0, n). */
  int: (n: number) => number;
  /** Float in [lo, hi). */
  range: (lo: number, hi: number) => number;
  /** Uniform pick. Throws on an empty array — a caller that can't
   *  guarantee non-empty should check first rather than get undefined. */
  pick: <T>(xs: readonly T[]) => T;
  /** True with probability p. */
  chance: (p: number) => boolean;
  /** Fisher-Yates, returns a new array. Order is seed-stable. */
  shuffle: <T>(xs: readonly T[]) => T[];
  /** Opaque snapshot of the generator's state, for save/restore. */
  snapshot: () => RngState;
};

export type RngState = [number, number, number, number];

/**
 * sfc32 — 128 bits of state, passes PractRand and BigCrush, and is
 * about as fast as `Math.random()`. Chosen over mulberry32 (which the
 * old engine used for the daily challenge) because 32 bits of state is
 * thin for a generator that gets called tens of thousands of times in
 * a four-minute run.
 */
export function makeRng(state: RngState): Rng {
  let [a, b, c, d] = state;

  const next = (): number => {
    a >>>= 0;
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;
    let t = (a + b) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    d = (d + 1) | 0;
    t = (t + d) | 0;
    c = (c + t) | 0;
    return (t >>> 0) / 4294967296;
  };

  const rng = next as Rng;
  rng.int = (n: number): number => Math.floor(next() * n);
  rng.range = (lo: number, hi: number): number => lo + next() * (hi - lo);
  rng.pick = <T,>(xs: readonly T[]): T => {
    if (xs.length === 0) throw new Error("rng.pick: empty array");
    return xs[Math.floor(next() * xs.length)]!;
  };
  rng.chance = (p: number): boolean => next() < p;
  rng.shuffle = <T,>(xs: readonly T[]): T[] => {
    const out = xs.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(next() * (i + 1));
      const tmp = out[i]!;
      out[i] = out[j]!;
      out[j] = tmp;
    }
    return out;
  };
  rng.snapshot = (): RngState => [a >>> 0, b >>> 0, c >>> 0, d >>> 0];
  return rng;
}

/** Build an Rng from a human-readable seed string. */
export function rngFromSeed(seed: string): Rng {
  const h = xmur3(seed);
  return makeRng([h(), h(), h(), h()]);
}

// ── Deterministic trig ────────────────────────────────────────────────
//
// Angles in the sim are **turns**, not radians: 0 = right, 0.25 = down,
// 0.5 = left, 0.75 = up, and 1.0 wraps to 0. Turns avoid a whole class
// of float drift (no π anywhere) and make pattern data readable — a
// six-way ring is `i / 6`, not `i * Math.PI / 3`.

/** Table resolution. 4096 entries at Float64 is 32KB, and the maximum
 *  angular error is 2π/4096/2 ≈ 0.00077 rad — well below one pixel of
 *  positional error over any distance this game draws. */
const TRIG_STEPS = 4096;

const SIN_TABLE = new Float64Array(TRIG_STEPS);
const COS_TABLE = new Float64Array(TRIG_STEPS);

// Built once at module load with the *host's* Math.sin. That looks like
// it reintroduces the very problem this file exists to solve — it
// doesn't, because the table is quantised: every lookup returns one of
// 4096 fixed entries, and any cross-engine disagreement lives far below
// the quantisation step. What matters is that the sim never
// interpolates between engine-computed values at runtime.
for (let i = 0; i < TRIG_STEPS; i++) {
  const rad = (i / TRIG_STEPS) * Math.PI * 2;
  SIN_TABLE[i] = Math.sin(rad);
  COS_TABLE[i] = Math.cos(rad);
}

/** Wrap a turn into [0, 1) and quantise it to the table. */
function index(turns: number): number {
  const wrapped = turns - Math.floor(turns);
  return Math.floor(wrapped * TRIG_STEPS) % TRIG_STEPS;
}

/** sin of an angle in turns. Deterministic across engines. */
export const sinT = (turns: number): number => SIN_TABLE[index(turns)]!;

/** cos of an angle in turns. Deterministic across engines. */
export const cosT = (turns: number): number => COS_TABLE[index(turns)]!;

/** Unit vector for an angle in turns. */
export function dirT(turns: number): { x: number; y: number } {
  return { x: cosT(turns), y: sinT(turns) };
}

/**
 * Angle in turns from (x0,y0) to (x1,y1). Uses a polynomial atan2
 * approximation rather than `Math.atan2` for the same cross-engine
 * reason — max error ~0.00013 turns, far tighter than the aim
 * tolerance of any pattern.
 */
export function angleT(x0: number, y0: number, x1: number, y1: number): number {
  const dx = x1 - x0;
  const dy = y1 - y0;
  if (dx === 0 && dy === 0) return 0;
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  // atan(z) ≈ z * (0.7853981634 - (|z| - 1) * (0.2447 + 0.0663 * |z|))
  // over [0,1], then folded into the correct octant.
  const z = ax > ay ? ay / ax : ax / ay;
  const a = z * (0.7853981634 - (z - 1) * (0.2447 + 0.0663 * z));
  let rad = ax > ay ? a : Math.PI / 2 - a;
  if (dx < 0) rad = Math.PI - rad;
  if (dy < 0) rad = -rad;
  const turns = rad / (Math.PI * 2);
  return turns - Math.floor(turns);
}

/** Shortest signed distance from `from` to `to`, in turns, in [-0.5, 0.5]. */
export function deltaT(from: number, to: number): number {
  let d = (to - from) % 1;
  if (d > 0.5) d -= 1;
  if (d < -0.5) d += 1;
  return d;
}

/** Deterministic hypot. `Math.sqrt` IS spec-exact (IEEE-754), unlike
 *  the transcendentals, so this one is safe to use directly. */
export const dist = (x0: number, y0: number, x1: number, y1: number): number =>
  Math.sqrt((x1 - x0) * (x1 - x0) + (y1 - y0) * (y1 - y0));

export const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;
